import React, { ReactNode, useState, useMemo } from 'react'

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'

import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis
} from '@dnd-kit/modifiers'

import {
  arrayMove,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'

import type { SxProps } from '@mui/material/styles'

import Box from 'components_new/atoms/Box'
import CardActionArea from 'components_new/atoms/Card/CardActionArea'
import Grid2 from '@mui/material/Unstable_Grid2'
import Icon from 'components_new/atoms/Icon'

import DndBlock, { DndBlockProps } from './DndBlock'
import DndSortableBlock from './DndSortableBlock'

interface DndBlocksProps {
  addBlock?: {
    height?: number | string
    onClick: () => void
    width?: number | string
  }
  editable: boolean
  handleDragEnd: (newOrder: string[]) => void
  items: {
    content: ReactNode
    disabled?: boolean
    id: string
  }[]
  orientation?: 'horizontal' | 'vertical' | 'grid'
  sx?: SxProps
}

const DndBlocks = (props: DndBlocksProps) => {
  const {
    addBlock,
    editable,
    handleDragEnd,
    items,
    orientation = 'vertical',
    sx
  } = props

  const isHorizontal = orientation === 'horizontal'
  const isGrid = orientation === 'grid'

  const [activeId, setActiveId] = useState<string | null>(null)
  const itemIds = useMemo(() => items.map((item) => item.id), [items])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  function handleDragStart(event: any) {
    const { active } = event

    if (editable) {
      setActiveId(active.id)
    }
  }

  const activeItem = items.find((item) => item.id === activeId)

  return (
    <Box sx={{ display: 'flex', minWidth: 0, ...sx }}>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={
          isGrid
            ? []
            : [isHorizontal ? restrictToHorizontalAxis : restrictToVerticalAxis]
        }
        onDragCancel={() => setActiveId(null)}
        onDragEnd={(event: any) => {
          const { active, over } = event

          setActiveId(null)

          if (active.id !== over.id) {
            const oldIndex = itemIds.indexOf(active.id)
            const newIndex = itemIds.indexOf(over.id)
            const newItemIds = arrayMove(itemIds, oldIndex, newIndex)

            handleDragEnd(newItemIds)

            return newItemIds
          }

          return itemIds
        }}
        onDragStart={handleDragStart}
        sensors={sensors}
      >
        <SortableContext
          items={itemIds}
          strategy={
            isGrid
              ? rectSortingStrategy
              : isHorizontal
                ? horizontalListSortingStrategy
                : verticalListSortingStrategy
          }
        >
          <BlocksContainer orientation={orientation}>
            {itemIds.map((id) => {
              const item = items.find((item) => item.id === id)

              return (
                <BlockContainer key={id} orientation={orientation}>
                  <DndSortableBlock
                    disabled={item?.disabled}
                    editable={items.length > 1 && editable}
                    orientation={orientation}
                    id={id}
                  >
                    {item?.content}
                  </DndSortableBlock>
                </BlockContainer>
              )
            })}

            {editable && addBlock ? (
              <BlockContainer orientation={orientation}>
                <Box
                  sx={{
                    flex: 0,
                    height:
                      addBlock.height ?? (isHorizontal || isGrid ? '100%' : 48),
                    minHeight: addBlock.height,
                    p: '2px',
                    width: addBlock.width
                  }}
                >
                  <CardActionArea
                    onClick={addBlock.onClick}
                    sx={{
                      alignItems: 'center',
                      borderRadius: 1,
                      color: 'divider',
                      display: 'flex',
                      height: '100%',
                      justifyConent: 'center',
                      opacity: activeId ? 0 : 1,
                      outline: '2px dashed',
                      outlineColor: 'divider',
                      position: 'relative',
                      transition: 'opacity 0.5s ease',
                      ['&:hover']: {
                        outline: '1px solid',
                        outlineColor: 'primary',
                        color: 'primary.main'
                      }
                    }}
                  >
                    <Icon color="inherit" name="Add" />
                  </CardActionArea>
                </Box>
              </BlockContainer>
            ) : null}
          </BlocksContainer>
        </SortableContext>
        <DragOverlay>
          {activeId && activeItem ? (
            <BlocksContainer isDragOverlay={true} orientation={orientation}>
              <BlockContainer orientation={orientation}>
                <DndBlock editable={editable} orientation={orientation}>
                  {activeItem.content}
                </DndBlock>
              </BlockContainer>
            </BlocksContainer>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Box>
  )
}

export default DndBlocks

interface BlocksContainer {
  children: ReactNode
  isDragOverlay?: boolean
  orientation: DndBlockProps['orientation']
}

const BlocksContainer = (props: BlocksContainer) => {
  const { children, isDragOverlay, orientation } = props

  const isHorizontal = orientation === 'horizontal'
  const isGrid = orientation === 'grid'
  const isVertical = orientation === 'vertical'

  if (orientation === 'grid') {
    if (isDragOverlay) return <>{children}</>

    return (
      <Grid2 container={true} spacing={1} sx={{ flex: 1 }}>
        {children}
      </Grid2>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isHorizontal || isGrid ? 'row' : 'column',
        flexWrap: isGrid ? 'wrap' : undefined,
        flex: 1,
        gap: isVertical ? 4 : 1,
        minWidth: 0,
        position: 'relative'
      }}
    >
      {children}
    </Box>
  )
}

interface BlockContainer {
  children: ReactNode
  orientation: DndBlockProps['orientation']
}

const BlockContainer = (props: BlockContainer) => {
  const { children, orientation } = props

  if (orientation === 'grid') return <Grid2 xs={3}>{children}</Grid2>

  return <>{children}</>
}
